import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
	Alert,
	Box,
	Chip,
	IconButton,
	Paper,
	Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";

import { planner } from "../../../planner-ui";

// Neutral format chip: alloy ground, hairline edge, muted ink.
const formatChipSx = {
	...planner.type.metaStrong,
	borderRadius: `${planner.radiusSm}px`,
	borderColor: planner.hairlineStrong,
	color: planner.inkSecondary,
	"& .MuiChip-icon": { color: planner.inkMuted },
};

interface UploadStepProps {
	selectedFile: File | null
	onFileSelect: (file: File) => void
	onRemoveFile: () => void
}

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ACCEPTED_TYPES = {
	"image/png": { icon: ImageIcon, label: "PNG Image" },
	"image/jpeg": { icon: ImageIcon, label: "JPEG Image" },
	"image/jpg": { icon: ImageIcon, label: "JPG Image" },
	"image/webp": { icon: ImageIcon, label: "WebP Image" },
	"application/pdf": { icon: PictureAsPdfIcon, label: "PDF Document" },
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
		icon: TableChartIcon,
		label: "Excel Spreadsheet",
	},
	"application/vnd.ms-excel": { icon: TableChartIcon, label: "Excel Spreadsheet" },
	"text/csv": { icon: TableChartIcon, label: "CSV File" },
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
		icon: DescriptionIcon,
		label: "Word Document",
	},
	"application/msword": { icon: DescriptionIcon, label: "Word Document" },
};

const ACCEPTED_EXTENSIONS = ".png,.jpg,.jpeg,.webp,.pdf,.xlsx,.xls,.csv,.docx,.doc";

const UploadStep: React.FC<UploadStepProps> = ({
	selectedFile,
	onFileSelect,
	onRemoveFile,
}) => {
	const [dragActive, setDragActive] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const validateFile = (file: File): string | null => {
		if (file.size > MAX_FILE_SIZE_BYTES) {
			return `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`;
		}

		const isAcceptedType = Object.keys(ACCEPTED_TYPES).includes(file.type);
		const extension = file.name.split(".").pop()?.toLowerCase();
		const isAcceptedExtension = ACCEPTED_EXTENSIONS.includes(`.${extension}`);

		if (!isAcceptedType && !isAcceptedExtension) {
			return "Unsupported file type. Please upload an image, PDF, Excel, or Word document.";
		}

		return null;
	};

	const handleFile = useCallback(
		(file: File) => {
			const validationError = validateFile(file);
			if (validationError) {
				setError(validationError);
				return;
			}
			setError(null);
			onFileSelect(file);
		},
		[onFileSelect]
	);

	const handleDrag = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === "dragenter" || e.type === "dragover") {
			setDragActive(true);
		} else if (e.type === "dragleave") {
			setDragActive(false);
		}
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setDragActive(false);

			if (e.dataTransfer.files && e.dataTransfer.files[0]) {
				handleFile(e.dataTransfer.files[0]);
			}
		},
		[handleFile]
	);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			handleFile(e.target.files[0]);
		}
	};

	const getFileIcon = (file: File) => {
		const typeInfo = ACCEPTED_TYPES[file.type as keyof typeof ACCEPTED_TYPES];
		if (typeInfo) {
			const IconComponent = typeInfo.icon;
			return <IconComponent sx={{ fontSize: 40, color: planner.navy }} />;
		}
		return <InsertDriveFileIcon sx={{ fontSize: 40, color: planner.navy }} />;
	};

	const getFileTypeLabel = (file: File) => {
		const typeInfo = ACCEPTED_TYPES[file.type as keyof typeof ACCEPTED_TYPES];
		return typeInfo?.label || "Document";
	};

	const formatFileSize = (bytes: number) => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	return (
		<Box>
			{error && (
				<Alert
					severity="error"
					icon={false}
					sx={{
						...planner.type.body,
						mb: 2,
						borderRadius: `${planner.radiusSm}px`,
						border: `${planner.borderWidth}px solid ${planner.unstaffed}55`,
						bgcolor: planner.unstaffedSoft,
						color: planner.unstaffed,
					}}
					onClose={() => setError(null)}
				>
					{error}
				</Alert>
			)}

			{!selectedFile ? (
				<Paper
					component="label"
					onDragEnter={handleDrag}
					onDragLeave={handleDrag}
					onDragOver={handleDrag}
					onDrop={handleDrop}
					elevation={0}
					sx={{
						p: 4,
						border: "2px dashed",
						borderColor: dragActive ? planner.accent : planner.hairlineStrong,
						borderRadius: `${planner.radius}px`,
						bgcolor: dragActive ? planner.accentSoft : planner.surfaceSubtle,
						cursor: "pointer",
						transition: "border-color 150ms ease",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						"&:hover": { borderColor: planner.accent },
					}}
				>
					<input
						type="file"
						accept={ACCEPTED_EXTENSIONS}
						onChange={handleChange}
						style={{ display: "none" }}
					/>

					<CloudUploadIcon sx={{ fontSize: 64, color: planner.accent, mb: 2 }} />

					<Typography sx={{ ...planner.type.title, color: planner.navy, mb: 0.5 }}>
						Drag & drop your file here
					</Typography>

					<Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>
						or click to browse
					</Typography>

					<Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
						<Chip icon={<ImageIcon />} label="Images" size="small" variant="outlined" sx={formatChipSx} />
						<Chip icon={<PictureAsPdfIcon />} label="PDF" size="small" variant="outlined" sx={formatChipSx} />
						<Chip icon={<TableChartIcon />} label="Excel/CSV" size="small" variant="outlined" sx={formatChipSx} />
						<Chip icon={<DescriptionIcon />} label="Word" size="small" variant="outlined" sx={formatChipSx} />
					</Box>

					<Typography
						sx={{ ...planner.type.meta, fontFamily: planner.fontMono, color: planner.inkMuted, mt: 2 }}
					>
						Maximum file size: {MAX_FILE_SIZE_MB}MB
					</Typography>
				</Paper>
			) : (
				<Paper
					elevation={0}
					sx={{
						p: 3,
						border: `${planner.borderWidth}px solid`,
						borderColor: planner.staffed,
						borderRadius: `${planner.radius}px`,
						bgcolor: planner.staffedSoft,
					}}
				>
					<Box display="flex" alignItems="center" gap={2}>
						{getFileIcon(selectedFile)}

						<Box flex={1}>
							<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
								{selectedFile.name}
							</Typography>
							<Typography
								sx={{ ...planner.type.meta, fontFamily: planner.fontMono, color: planner.inkSecondary }}
							>
								{getFileTypeLabel(selectedFile)} • {formatFileSize(selectedFile.size)}
							</Typography>
						</Box>

						<IconButton
							onClick={onRemoveFile}
							size="small"
							sx={{ color: planner.inkMuted, "&:hover": { color: planner.unstaffed } }}
						>
							<DeleteIcon />
						</IconButton>
					</Box>
				</Paper>
			)}

			<Alert
				severity="info"
				icon={false}
				sx={{
					mt: 3,
					borderRadius: `${planner.radiusSm}px`,
					border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
					bgcolor: planner.unplannedSoft,
					color: planner.inkSecondary,
				}}
			>
				<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink, mb: 0.5 }}>
					Tips for best results:
				</Typography>
				<Typography
					component="ul"
					sx={{ ...planner.type.body, color: planner.inkSecondary, m: 0, pl: 2 }}
				>
					<li>Ensure the document clearly shows Product/SKU, Quantity, Workstation, and Start/End times</li>
					<li>For images, make sure text is legible and not blurry</li>
					<li>Tables and structured formats work best</li>
				</Typography>
			</Alert>
		</Box>
	);
};

export default UploadStep;


